
import { fetchPosts, fetchPostBySlug } from '@/services/wordpress';
import { isSSR } from '@/lib/isSSR';
import { savePostsToCaches, saveSinglePostToCaches } from './cacheUtils';

let isPrefetching = false;

export const debounce = (fn: Function, ms = 1000) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const prefetchBlogPosts = async (params: any, cacheKey: string): Promise<void> => {
  if (isPrefetching) return;
  
  try {
    isPrefetching = true;
    console.log('Prefetching blog posts in background to update cache');
    
    // Separa i parametri di categoria e ricerca per la nuova logica di filtro
    const { category, search, ...otherParams } = params;
    
    // Fase 1: Recupera i post filtrati per categoria (senza ricerca)
    const categoryParams = {
      ...otherParams,
      category,
      // Se stiamo cercando, otteniamo più post per filtrare lato client
      perPage: search ? 100 : otherParams.perPage || 10
    };
    
    console.log('Prefetching per categoria:', categoryParams);
    const categoryFilteredPosts = await fetchPosts(categoryParams);
    
    // Fase 2: Applica ricerca testuale ai risultati filtrati per categoria
    let freshPosts = categoryFilteredPosts;
    
    if (search) {
      console.log(`Applicazione ricerca testuale "${search}" a ${categoryFilteredPosts.length} post filtrati per categoria`);
      
      const lowerSearch = search.toLowerCase();
      freshPosts = categoryFilteredPosts.filter(post => 
        post.title.toLowerCase().includes(lowerSearch) || 
        post.excerpt.toLowerCase().includes(lowerSearch) || 
        (post.content && post.content.toLowerCase().includes(lowerSearch))
      );
      
      // Applica paginazione ai risultati della ricerca
      if (otherParams.page && otherParams.perPage) {
        const page = parseInt(otherParams.page, 10) || 1;
        const perPage = parseInt(otherParams.perPage, 10) || 10;
        const startIndex = (page - 1) * perPage;
        freshPosts = freshPosts.slice(startIndex, startIndex + perPage);
      }
    }
    
    if (freshPosts.length > 0) {
      const clientCacheKey = `client_${cacheKey}`;
      savePostsToCaches(cacheKey, clientCacheKey, freshPosts);
      console.log('Client cache updated via prefetch');
    }
  } catch (error) {
    console.error('Error prefetching blog posts:', error);
  } finally {
    isPrefetching = false;
  }
};

export const prefetchSinglePost = async (slug: string): Promise<void> => {
  console.log(`Prefetching blog post in background for slug: ${slug}`);
  const freshPost = await fetchPostBySlug(slug);
  
  if (freshPost) {
    saveSinglePostToCaches(slug, freshPost);
    console.log('Post cache updated via prefetch');
  }
};

export const debouncedPrefetchPosts = debounce(prefetchBlogPosts, 5000);
export const debouncedPrefetchSinglePost = debounce(prefetchSinglePost, 5000);
